import { recruiterEnv } from '@/config/env';

/**
 * Data layer da visualização EXTERNA de currículo/laudo compartilhado
 * (EU-594, complemento — comentário 23123). As rotas são PÚBLICAS
 * (`/public/shared-documents/*`): o destinatário do link não tem sessão, então
 * tudo aqui usa `fetch` cru SEM Authorization — nunca o `fetchClient` do SDK
 * (ele injeta bearer do Keycloak e redireciona em 401).
 *
 * Contrato do BFF (candidate-files.routes.ts):
 * - `GET /public/shared-documents/:token/meta` → 200 `{ kind, candidateName,
 *   candidateEmail, expiresAt }` | 410 (expirado OU inválido — sem NENHUM
 *   dado, RN-38/40) | 503.
 * - `GET /public/shared-documents/:token` → bytes `application/pdf` já com
 *   marca d'água server-side | 410 | 503.
 */

export type SharedDocumentKind = 'resume' | 'attestation';

export interface SharedDocumentMeta {
  kind: SharedDocumentKind;
  /** Nome (social preferencial) — null em tokens antigos (RN-31 fallback). */
  candidateName: string | null;
  candidateEmail: string | null;
  /** Instante ISO em que o link expira. */
  expiresAt: string;
}

export class SharedDocumentError extends Error {
  readonly name = 'SharedDocumentError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/** 410 = link expirado OU inválido — mesmo estado na UI (RN-40). */
export function isGoneError(error: unknown): boolean {
  return error instanceof SharedDocumentError && error.status === 410;
}

function publicUrl(path: string): string {
  return `${recruiterEnv.apiUrl.replace(/\/+$/, '')}${path}`;
}

/** Metadados do link (tipo + identidade do candidato para o header). */
export async function fetchSharedDocumentMeta(token: string): Promise<SharedDocumentMeta> {
  const res = await fetch(publicUrl(`/public/shared-documents/${encodeURIComponent(token)}/meta`));
  if (!res.ok) {
    throw new SharedDocumentError(
      `Falha ao carregar os metadados do link (HTTP ${res.status})`,
      res.status
    );
  }
  return (await res.json()) as SharedDocumentMeta;
}

/**
 * Baixa os bytes do PDF compartilhado (marca d'água server-side) e devolve um
 * object URL `blob:`. O CALLER é dono do URL e deve revogá-lo
 * (`URL.revokeObjectURL`) no cleanup.
 */
export async function fetchSharedDocumentUrl(token: string): Promise<string> {
  const res = await fetch(publicUrl(`/public/shared-documents/${encodeURIComponent(token)}`));
  if (!res.ok) {
    throw new SharedDocumentError(`Falha ao baixar o documento (HTTP ${res.status})`, res.status);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
